/**
 * Helpers for reading a match's participants from the `teams` / `teamNames`
 * structure (the single source of truth), replacing the retired positional
 * player1Id..player4Id / player1Name..player4Name fields.
 *
 * `teams` = [[player1Id, player3Id], [player2Id, player4Id]] for standard
 * matches; bye teams may be ragged (1-3 ids). `teamNames` mirrors `teams`
 * position-for-position and is resolved server-side.
 */

import type { Match } from "../types/index";

/** Parse a match's `teams` (parsed array or JSON string) into number[][]. */
export const parseTeams = (match: Pick<Match, "teams">): number[][] => {
  const raw: unknown = (match as { teams?: unknown }).teams;
  if (Array.isArray(raw)) return raw as number[][];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Display name for one team ("Alice & Bob", or a single name), built from the
 * server-resolved `teamNames`. Falls back to `fallback` when the team is empty.
 */
export const teamDisplayName = (match: Match, index: number, fallback = "TBD"): string => {
  const names = (match.teamNames?.[index] || []).filter(Boolean);
  return names.length ? names.join(" & ") : fallback;
};

/**
 * Unique participants of a match as `{ id, name }`, deduped by id and ordered by
 * team position. Used for bye matches (1-3 players) where the team shape varies.
 */
export const matchParticipants = (match: Match): Array<{ id: number; name: string }> => {
  const teams = parseTeams(match);
  const names = match.teamNames || [];
  const seen = new Set<number>();
  const out: Array<{ id: number; name: string }> = [];

  teams.forEach((team, ti) => {
    team.forEach((id, pi) => {
      if (typeof id === "number" && id > 0 && !seen.has(id)) {
        seen.add(id);
        out.push({ id, name: names[ti]?.[pi] || `Player ${id}` });
      }
    });
  });

  return out;
};
